'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import PlannerHeaderActions from '@/components/marketing/PlannerHeaderActions';
import { useRouter } from 'next/navigation';

import { 
  Upload, 
  Download,
  Trash2, 
  Layers, 
  Shirt, 
  Zap, 
  Sparkles, 
  Palette, 
  Undo,
  ArrowRight,
  Loader2,
  Scissors,
  ChevronDown,
  Instagram
} from 'lucide-react';



import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

// Dynamic imports or client-side checks are often needed for Fabric in Next.js
// We will import fabric inside useEffect to ensure window is defined
let FabricCanvas = null;
let FabricImage = null;

// --- Placeholder Data for Essential Wardrobe ---
const ESSENTIAL_WARDROBE = [
  { id: 'item-1', src: '/plannerItems/clothingItems/1.png', name: 'Item 1' },
  { id: 'item-2', src: '/plannerItems/clothingItems/2.png', name: 'Item 2' },
  { id: 'item-3', src: '/plannerItems/clothingItems/3.png', name: 'Item 3' },
  { id: 'item-4', src: '/plannerItems/clothingItems/4.png', name: 'Item 4' },
  { id: 'item-5', src: '/plannerItems/clothingItems/5.png', name: 'Item 5' },
  { id: 'item-6', src: '/plannerItems/clothingItems/6.png', name: 'Item 6' },
  { id: 'item-7', src: '/plannerItems/clothingItems/7.png', name: 'Item 7' },
  { id: 'item-8', src: '/plannerItems/clothingItems/8.png', name: 'Item 8' },
  { id: 'item-9', src: '/plannerItems/clothingItems/9.png', name: 'Item 9' },
  { id: 'item-10', src: '/plannerItems/clothingItems/10.png', name: 'Item 10' },
  { id: 'item-11', src: '/plannerItems/clothingItems/11.png', name: 'Item 11' },
  { id: 'item-12', src: '/plannerItems/clothingItems/12.png', name: 'Item 12' },
  { id: 'item-13', src: '/plannerItems/clothingItems/13.png', name: 'Item 13' },
  { id: 'item-14', src: '/plannerItems/clothingItems/14.png', name: 'Item 14' },
  { id: 'item-15', src: '/plannerItems/clothingItems/15.png', name: 'Item 15' },
  { id: 'item-16', src: '/plannerItems/clothingItems/16.png', name: 'Item 16' },
  { id: 'item-17', src: '/plannerItems/clothingItems/17.png', name: 'Item 17' },
  { id: 'item-18', src: '/plannerItems/clothingItems/18.png', name: 'Item 18' },
  { id: 'item-19', src: '/plannerItems/clothingItems/19.png', name: 'Item 19' },
  { id: 'item-20', src: '/plannerItems/clothingItems/20.png', name: 'Item 20' },
  { id: 'item-21', src: '/plannerItems/clothingItems/21.png', name: 'Item 21' },
  { id: 'item-22', src: '/plannerItems/clothingItems/22.png', name: 'Item 22' },
  { id: 'item-23', src: '/plannerItems/clothingItems/23.png', name: 'Item 23' },
  { id: 'item-24', src: '/plannerItems/clothingItems/24.png', name: 'Item 24' },
  { id: 'item-25', src: '/plannerItems/clothingItems/25.png', name: 'Item 25' },
  { id: 'item-26', src: '/plannerItems/clothingItems/26.png', name: 'Item 26' },
  { id: 'item-27', src: '/plannerItems/clothingItems/27.png', name: 'Item 27' },
  { id: 'item-28', src: '/plannerItems/clothingItems/28.png', name: 'Item 28' },
  { id: 'item-29', src: '/plannerItems/clothingItems/29.png', name: 'Item 29' },
  { id: 'item-30', src: '/plannerItems/clothingItems/30.png', name: 'Item 30' },
  { id: 'item-31', src: '/plannerItems/clothingItems/31.png', name: 'Item 31' },
  { id: 'item-32', src: '/plannerItems/clothingItems/32.png', name: 'Item 32' },
  { id: 'item-32-1', src: '/plannerItems/clothingItems/32-1.png', name: 'Item 32-1' },
];

export default function OutfitPlannerPage() {
  const router = useRouter();
  
  // --- State Management ---
  const canvasRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activePalette, setActivePalette] = useState([]);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [uploadedImageSrc, setUploadedImageSrc] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [draggedItemSrc, setDraggedItemSrc] = useState(null);
  
  // Export Dialog State
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportTitle, setExportTitle] = useState('');
  const [showColorGrid, setShowColorGrid] = useState(true);
  
  // Undo/Redo History
  const [history, setHistory] = useState([]);
  const isUndoingRef = useRef(false);
  const isInitializedRef = useRef(false);
  const shouldBlockNavigationRef = useRef(false);
  
  // --- Initialization ---
  useEffect(() => {
    // Prevent double initialization
    if (isInitializedRef.current) return;
    
    let newCanvas = null;
    let resizeHandler = null;
    
    // Import fabric.js only on client side - v6 uses named exports
    import('fabric').then((fabricModule) => {
      console.log("Fabric module loaded:", Object.keys(fabricModule));
      
      // Handle different import structures (ESM vs CJS interop)
      // Fabric v6 typically has named exports, but sometimes under .default in Next.js
      const Canvas = fabricModule.Canvas || fabricModule.default?.Canvas;
      const Image = fabricModule.Image || fabricModule.default?.Image;
      
      FabricCanvas = Canvas;
      FabricImage = Image;
      
      if (!Canvas || !Image) {
        console.error('Fabric.js not loaded correctly. Available keys:', Object.keys(fabricModule));
        return;
      }
      
      if (!canvasRef.current) return;
      
      // Check if canvas element already has a fabric instance
      // Dispose any existing canvas first
      if (canvasRef.current.__canvas) {
        try {
          canvasRef.current.__canvas.dispose();
        } catch (e) {
          console.warn('Error disposing existing canvas:', e);
        }
      }
      
      // Create new canvas instance
      newCanvas = new Canvas(canvasRef.current, {
        backgroundColor: '#111111', // Dark canvas background
        selection: true,
        preserveObjectStacking: true,
      });

      // Resize canvas to fit parent container
      resizeHandler = () => {
        const parent = document.getElementById('canvas-container');
        if (parent && newCanvas) {
          newCanvas.setDimensions({
            width: parent.clientWidth,
            height: parent.clientHeight
          });
        }
      };

      window.addEventListener('resize', resizeHandler);
      resizeHandler();

      // Selection events
      newCanvas.on('selection:created', (e) => handleObjectSelection(e.selected[0]));
      newCanvas.on('selection:updated', (e) => handleObjectSelection(e.selected[0]));
      newCanvas.on('selection:cleared', () => {
        setSelectedObject(null);
        setActivePalette([]);
      });

      // History events
      const saveHistory = () => {
         if (isUndoingRef.current) return;
         // Limit history to 10 states
         const json = newCanvas.toJSON();
         setHistory(prev => {
           const newHistory = [...prev, json];
           if (newHistory.length > 10) newHistory.shift();
           return newHistory;
         });
      };

      newCanvas.on('object:added', saveHistory);
      newCanvas.on('object:modified', saveHistory);
      newCanvas.on('object:removed', saveHistory);

      setCanvas(newCanvas);
      isInitializedRef.current = true;
    });

    return () => {
      // Reset initialization flag
      isInitializedRef.current = false;
      
      // Cleanup resize handler
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
      
      // Dispose canvas
      if (newCanvas) {
        try {
          newCanvas.dispose();
          newCanvas = null;
        } catch (e) {
          console.warn('Error disposing canvas in cleanup:', e);
        }
      }
      
      // Also check and dispose canvas from element if it exists
      if (canvasRef.current?.__canvas) {
        try {
          canvasRef.current.__canvas.dispose();
        } catch (e) {
          console.warn('Error disposing canvas from element:', e);
        }
      }
    };
  }, []);

  // Ensure canvas resizes properly after layout changes
  useEffect(() => {
    if (!canvas) return;

    const resizeCanvas = () => {
      const parent = document.getElementById('canvas-container');
      if (parent && canvas) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          canvas.setDimensions({
            width: parent.clientWidth,
            height: parent.clientHeight
          });
          canvas.renderAll();
        });
      }
    };

    // Initial resize with a small delay to ensure layout is ready
    const timeoutId = setTimeout(resizeCanvas, 100);
    
    // Resize on window resize
    window.addEventListener('resize', resizeCanvas);
    
    // Use ResizeObserver for more accurate container size tracking
    const parent = document.getElementById('canvas-container');
    let resizeObserver = null;
    
    if (parent && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        resizeCanvas();
      });
      resizeObserver.observe(parent);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', resizeCanvas);
      if (resizeObserver && parent) {
        resizeObserver.unobserve(parent);
      }
    };
  }, [canvas]);

  // Keyboard event handler for Delete key
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Delete or Backspace key
      if ((e.key === 'Delete' || e.key === 'Backspace') && canvas) {
        // Don't delete if user is typing in an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          return;
        }
        
        const activeObjects = canvas.getActiveObjects();
        if (activeObjects.length) {
          e.preventDefault();
          canvas.discardActiveObject();
          activeObjects.forEach((obj) => canvas.remove(obj));
          canvas.renderAll();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [canvas]);

  // Navigation warning when leaving page with unsaved changes
  useEffect(() => {
    if (!canvas) return;

    const handleBeforeUnload = (e) => {
      const hasItems = canvas.getObjects().length > 0;
      if (hasItems) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // For older browsers
      }
    };

    const handlePopState = (e) => {
      const hasItems = canvas.getObjects().length > 0;
      if (hasItems) {
        const confirmed = window.confirm(
          'You have items on the canvas. Your changes will be lost if you navigate away. Do you want to continue?'
        );
        if (!confirmed) {
          // Push current state back to prevent navigation
          window.history.pushState(null, '', window.location.href);
        }
      }
    };

    // Add initial state to history for popstate to work
    window.history.pushState(null, '', window.location.href);

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [canvas]);

  // --- Helpers ---

  const handleObjectSelection = async (obj) => {
    setSelectedObject(obj);
    
    // Extract colors if it's an image
    if (obj.type === 'image') {
      try {
        // We need a real image element for color thief, standard fabric image has an element
        const imgElement = obj._element;
        // In a real app, we'd import ColorThief dynamically or ensure it's loaded
        const ColorThief = (await import('colorthief')).default;
        const colorThief = new ColorThief();
        
        // Make sure image is loaded and accessible (CORS can be tricky here with external URLs)
        // For demo purposes, we might need a proxy or use local images. 
        // We'll assume standard CORS handling or try/catch.
        if (imgElement.complete) {
           const palette = colorThief.getPalette(imgElement, 4);
           const hexPalette = palette.map((rgb) => 
             `#${((1 << 24) + (rgb[0] << 16) + (rgb[1] << 8) + rgb[2]).toString(16).slice(1)}`
           );
           setActivePalette(hexPalette);
        }
      } catch (e) {
        console.warn("Could not extract colors (likely CORS issue on placeholder images)", e);
        // Fallback random palette for demo visualization if CORS fails
        setActivePalette(['#E5989B', '#F5C5C2', '#6D6875', '#B5838D']); 
      }
    }
  };

  // --- Core Features ---

  // 1. Magic Background Remover & Upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !canvas) return;

    setIsProcessing(true);
    let imageUrl = null;
    let processedUrl = null;
    let rawUrl = null;

    try {
      console.log("Starting background removal...");
      // Import imgly dynamically to avoid SSR issues
      const imglyModule = await import('@imgly/background-removal');
      const removeBackground = imglyModule.removeBackground || imglyModule.default;
      
      if (typeof removeBackground !== 'function') {
        throw new Error(`imgly removeBackground is not a function: ${typeof removeBackground}`);
      }
      
      imageUrl = URL.createObjectURL(file);
      setUploadedImageSrc(imageUrl); 

      console.log("Processing image...", imageUrl);
      // Process: Remove Background with timeout
      const config = {
        debug: true,
        progress: (key, current, total) => {
          console.log(`Downloading ${key}: ${current} of ${total}`);
        }
      };
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Background removal timeout after 30 seconds')), 30000)
      );
      
      const blob = await Promise.race([
        removeBackground(imageUrl, config),
        timeoutPromise
      ]);
      
      console.log("Background removed, blob created:", blob);
      processedUrl = URL.createObjectURL(blob);

      // Add to Canvas
      if (!FabricImage) {
        console.error('Fabric.js Image not loaded');
        throw new Error('Fabric.js Image not loaded');
      }
      
      console.log("Adding to canvas...");
      const img = await FabricImage.fromURL(processedUrl);
      console.log("Image loaded from URL:", img);
      img.scaleToWidth(300); // Default size
      img.set({
          cornerColor: '#ffffff',
          cornerStrokeColor: '#0203eb', // Violet-500
          borderColor: '#0203eb',
          cornerSize: 12,
          transparentCorners: false,
          padding: 10,
          cornerStyle: 'circle'
      });
      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
      console.log("Image added to canvas successfully");

    } catch (error) {
      console.error("BG Removal Failed:", error);
      // Fallback: Add raw image if BG removal fails
      if (!FabricImage) {
        console.error('Fabric.js Image not loaded for fallback');
        return;
      }
      
      try {
        rawUrl = rawUrl || URL.createObjectURL(file);
        console.log("Adding raw image fallback...");
        const img = await FabricImage.fromURL(rawUrl);
        img.scaleToWidth(300);
        img.set({
            cornerColor: '#ffffff',
            cornerStrokeColor: '#0203eb',
            borderColor: '#0203eb',
            cornerSize: 12,
            transparentCorners: false,
            padding: 10,
            cornerStyle: 'circle'
        });
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        console.log("Raw image added to canvas successfully");
      } catch (err) {
        console.error("Error adding raw image to canvas:", err);
      }
    } finally {
      // Always reset processing state and cleanup URLs
      setIsProcessing(false);
      
      // Cleanup object URLs to prevent memory leaks
      if (imageUrl) URL.revokeObjectURL(imageUrl);
      if (processedUrl) URL.revokeObjectURL(processedUrl);
      if (rawUrl) URL.revokeObjectURL(rawUrl);
    }
  };

  // 2. Add Essential Wardrobe Item
  const addEssentialItem = async (src) => {
    if (!canvas || !FabricImage) return;
    
    try {
      // CORS setting is crucial for canvas manipulation
      // In Fabric.js v6, fromURL returns a promise and takes options as second argument
      const img = await FabricImage.fromURL(src, { crossOrigin: 'anonymous' });
      
      img.scaleToWidth(200);
      img.set({
          cornerColor: '#ffffff',
          cornerStrokeColor: '#0203eb',
          borderColor: '#0203eb',
          cornerSize: 12,
          transparentCorners: false,
          padding: 10,
          cornerStyle: 'circle'
      });
      canvas.add(img);
      canvas.centerObject(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
    } catch (error) {
      console.error("Failed to add item to canvas:", error);
    }
  };

  // 3. Canvas Controls
  const deleteActiveObject = () => {
    const activeObjects = canvas.getActiveObjects();
    if (activeObjects.length) {
      canvas.discardActiveObject();
      activeObjects.forEach((obj) => canvas.remove(obj));
    }
  };

  const moveLayer = (direction) => {
    const activeObj = canvas.getActiveObject();
    if (!activeObj) return;
    
    if (direction === 'up') {
      // Fabric.js v6 uses canvas methods for z-index
      canvas.bringObjectForward(activeObj);
    } else {
      canvas.sendObjectBackwards(activeObj);
    }
    canvas.renderAll();
  };

  const undo = async () => {
    if (history.length === 0 || !canvas) return;
    
    isUndoingRef.current = true;
    const lastState = history[history.length - 2]; // Go to previous state
    
    const newHistory = [...history];
    newHistory.pop(); // Remove current state
    setHistory(newHistory);
    
    try {
      if (newHistory.length > 0) {
          const stateToRestore = newHistory[newHistory.length - 1];
          await canvas.loadFromJSON(stateToRestore);
          canvas.renderAll();
      } else {
          canvas.clear();
          canvas.backgroundColor = '#111111';
          canvas.renderAll();
      }
    } catch (e) {
      console.error("Undo failed", e);
    }
    
    isUndoingRef.current = false;
  };

  const handleExport = () => {
    if (!canvas) return;
    
    // 1. Calculate content bounding box
    const objects = canvas.getObjects();
    if (objects.length === 0) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    objects.forEach(obj => {
      const br = obj.getBoundingRect();
      minX = Math.min(minX, br.left);
      minY = Math.min(minY, br.top);
      maxX = Math.max(maxX, br.left + br.width);
      maxY = Math.max(maxY, br.top + br.height);
    });

    // Add padding
    const padding = 50;
    const width = maxX - minX + (padding * 2);
    const height = maxY - minY + (padding * 2);
    
    // Save current state
    const originalBg = canvas.backgroundColor;
    const originalViewport = canvas.viewportTransform;
    
    // Set white background for JPG
    canvas.backgroundColor = '#ffffff';
    
    try {
        // Export cropped area at high resolution
        const dataURL = canvas.toDataURL({
          format: 'jpeg',
          quality: 0.95,
          left: minX - padding,
          top: minY - padding,
          width: width,
          height: height,
          multiplier: 3 // High resolution multiplier (3x)
        });
        
        // Trigger download
        const link = document.createElement('a');
        link.download = 'outfit-plan.jpg';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (e) {
        console.error("Export failed", e);
    } finally {
        // Restore state
        canvas.backgroundColor = originalBg;
        canvas.renderAll();
    }
  };

  const handleGenerateModel = () => {
    if (!canvas) return;
    
    // 1. Calculate content bounding box
    const objects = canvas.getObjects();
    if (objects.length === 0) {
      // If no objects, just redirect
      router.push('/signup');
      return;
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    objects.forEach(obj => {
      const br = obj.getBoundingRect();
      minX = Math.min(minX, br.left);
      minY = Math.min(minY, br.top);
      maxX = Math.max(maxX, br.left + br.width);
      maxY = Math.max(maxY, br.top + br.height);
    });

    // Add padding
    const padding = 50;
    const width = maxX - minX + (padding * 2);
    const height = maxY - minY + (padding * 2);
    
    // Save current state
    const originalBg = canvas.backgroundColor;
    
    // Set white background for JPG
    canvas.backgroundColor = '#ffffff';
    
    try {
        // Export cropped area at high resolution
        const dataURL = canvas.toDataURL({
          format: 'jpeg',
          quality: 0.95,
          left: minX - padding,
          top: minY - padding,
          width: width,
          height: height,
          multiplier: 3 // High resolution multiplier (3x)
        });
        
        // Trigger download
        const link = document.createElement('a');
        link.download = 'outfit-plan.jpg';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Small delay to ensure download starts, then redirect
        setTimeout(() => {
          router.push('/signup');
        }, 100);
    } catch (e) {
        console.error("Export failed", e);
        // Redirect even if export fails
        router.push('/signup');
    } finally {
        // Restore state
        canvas.backgroundColor = originalBg;
        canvas.renderAll();
    }
  };

  const handleInstagramExport = async () => {
    if (!canvas) return;
    setShowExportDialog(false); // Close dialog

    // Save original state
    const originalBg = canvas.backgroundColor;
    const objects = canvas.getObjects();
    
    // Store original shadows and references to objects we'll modify
    const originalShadows = objects.map(obj => obj.shadow);
    const addedElements = []; // Track elements we add (title, color grid)

    try {
      // 1. Apply "Instagram Style" - Shadows & Outlines
      
      // Load Fabric classes if needed
      // We assume FabricCanvas/FabricImage are available from init, but we might need Shadow/Text
      // Since we're inside the component where we loaded fabric, we can use the instances or primitives if available
      // But getting the class constructors might be tricky if not saved globally. 
      // However, canvas.getObjects() returns instances that have methods.
      
      // We need access to fabric.Shadow and fabric.Text/IText
      // Let's grab them from the module import if possible, or rely on the object instances
      
      // Dynamic import to ensure we have classes
      const fabricModule = await import('fabric');
      const fabric = fabricModule.default || fabricModule;
      const Shadow = fabric.Shadow;
      const Text = fabric.Text || fabric.IText;

      objects.forEach((obj, index) => {
        // Add white outline (sticker effect)
        // Note: stroke on images works differently in Fabric, might need a filter or group
        // For simplicity, let's just add a nice shadow
        obj.set({
          shadow: new Shadow({
            color: 'rgba(0,0,0,0.3)',
            blur: 30,
            offsetX: 15,
            offsetY: 15
          }),
          // Stroke doesn't always work well on images directly without SVG filters in Fabric
          // so we'll stick to a strong shadow and maybe rounded corners if possible
        });
      });

      // 2. Calculate Bounds of Items (Before adding title)
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      canvas.getObjects().forEach(obj => {
        const br = obj.getBoundingRect();
        minX = Math.min(minX, br.left);
        minY = Math.min(minY, br.top);
        maxX = Math.max(maxX, br.left + br.width);
        maxY = Math.max(maxY, br.top + br.height);
      });

      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;
      
      // Target Aspect Ratio 3:4 (0.75)
      const targetRatio = 3/4;
      const contentRatio = contentWidth / contentHeight;

      let exportWidth, exportHeight;
      const padding = 100; // Generous padding for Insta

      if (contentRatio > targetRatio) {
        // Content is wider than target, width dictates height
        exportWidth = contentWidth + (padding * 2);
        exportHeight = exportWidth / targetRatio;
      } else {
        // Content is taller, height dictates width
        exportHeight = contentHeight + (padding * 2);
        exportWidth = exportHeight * targetRatio;
      }

      // Center the crop area around the content center
      const contentCenterX = minX + (contentWidth / 2);
      const contentCenterY = minY + (contentHeight / 2);

      const exportLeft = contentCenterX - (exportWidth / 2);
      const exportTop = contentCenterY - (exportHeight / 2);

      // 3. Add Title (Scaled to image size)
      if (exportTitle && Text) {
        // Calculate font size based on export width (e.g., 8% of width)
        const calculatedFontSize = Math.round(exportWidth * 0.08);
        
        // Ensure font is loaded before creating text
        await document.fonts.ready;
        
        const titleObj = new Text(exportTitle, {
          fontSize: calculatedFontSize,
          fontFamily: '"Space Grotesk", sans-serif',
          fontWeight: '600',
          fill: '#000000',
          left: exportLeft + (padding * 0.8), // Position relative to crop area
          top: exportTop + (padding * 0.8),
          originX: 'left',
          originY: 'top'
        });
        canvas.add(titleObj);
        addedElements.push(titleObj);
      }

      // 4. Extract colors and create color grid if enabled
      let allColors = [];
      if (showColorGrid) {
        try {
          const ColorThief = (await import('colorthief')).default;
          const colorThief = new ColorThief();
          
          // Extract colors from all image objects
          for (const obj of objects) {
            if (obj.type === 'image' && obj._element) {
              try {
                const imgElement = obj._element;
                if (imgElement.complete && imgElement.naturalWidth > 0) {
                  const palette = colorThief.getPalette(imgElement, 3); // Get 3 colors per image
                  const hexPalette = palette.map((rgb) => 
                    `#${((1 << 24) + (rgb[0] << 16) + (rgb[1] << 8) + rgb[2]).toString(16).slice(1)}`
                  );
                  allColors.push(...hexPalette);
                }
              } catch (e) {
                console.warn('Could not extract colors from image:', e);
              }
            }
          }
          
          // Limit to 12 colors max and remove duplicates
          const uniqueColors = [...new Set(allColors)].slice(0, 12);
          
          if (uniqueColors.length > 0 && fabric.Rect) {
            const Rect = fabric.Rect;
            const gridHeight = Math.round(exportWidth * 0.12); // 12% of width
            const gridTop = exportTop + exportHeight - gridHeight; // Stick to absolute bottom
            const numColors = uniqueColors.length;
            const colorWidth = exportWidth / numColors;
            
            // Add background rectangle for color grid
            const bgRect = new Rect({
              left: exportLeft,
              top: gridTop,
              width: exportWidth,
              height: gridHeight,
              fill: '#ffffff',
              selectable: false,
              evented: false
            });
            canvas.add(bgRect);
            addedElements.push(bgRect);
            
            // Add color swatches
            uniqueColors.forEach((color, index) => {
              const colorRect = new Rect({
                left: exportLeft + (index * colorWidth),
                top: gridTop,
                width: colorWidth,
                height: gridHeight,
                fill: color,
                selectable: false,
                evented: false
              });
              canvas.add(colorRect);
              addedElements.push(colorRect);
            });
          }
        } catch (e) {
          console.warn('Could not create color grid:', e);
        }
      }

      // Set stylized background for export
      canvas.backgroundColor = '#f3f4f6'; // Light gray sleek background

      canvas.renderAll();

      // 5. Export at high resolution for crisp text
      const dataURL = canvas.toDataURL({
        format: 'png', // PNG for better quality with text
        quality: 1,
        left: exportLeft,
        top: exportTop,
        width: exportWidth,
        height: exportHeight,
        multiplier: 4 // 4x for ultra-high resolution text
      });

      // Download
      const link = document.createElement('a');
      link.download = `outfit-insta-${Date.now()}.png`;
      link.href = dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error("Instagram export failed:", err);
    } finally {
      // Restore State - Remove added elements and restore original shadows
      try {
        // Remove all elements we added (title, color grid)
        addedElements.forEach(element => {
          try {
            canvas.remove(element);
          } catch (e) {
            // Element might already be removed, ignore
          }
        });
        
        // Restore original shadows - only restore if object still exists on canvas
        const currentObjects = canvas.getObjects();
        objects.forEach((obj, index) => {
          // Check if object still exists on canvas
          if (currentObjects.includes(obj)) {
            const originalShadow = originalShadows[index];
            obj.set({
              shadow: originalShadow !== undefined ? originalShadow : null
            });
          }
        });
        
        // Restore background
        canvas.backgroundColor = originalBg;
        canvas.renderAll();
      } catch (restoreErr) {
        console.error("Error restoring canvas state:", restoreErr);
        // Fallback: save current state before trying to restore
        try {
          // Get current objects before removing added elements
          const currentObjects = canvas.getObjects();
          const objectsToKeep = currentObjects.filter(obj => !addedElements.includes(obj));
          
          // Clear canvas and re-add only original objects
          canvas.clear();
          objectsToKeep.forEach(obj => {
            canvas.add(obj);
          });
          
          // Restore shadows
          objects.forEach((obj, index) => {
            if (objectsToKeep.includes(obj)) {
              const originalShadow = originalShadows[index];
              obj.set({
                shadow: originalShadow !== undefined ? originalShadow : null
              });
            }
          });
          
          canvas.backgroundColor = originalBg;
          canvas.renderAll();
        } catch (fallbackErr) {
          console.error("Fallback restoration also failed:", fallbackErr);
        }
      }
    }
  };

  // 4. Drag and Drop Handlers
  const handleDragStart = (e, src) => {
    setDraggedItemSrc(src);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', src);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    if (!canvas || !FabricImage) return;

    // Check if it's a file drop
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        // Create a synthetic event for handleImageUpload
        const syntheticEvent = {
          target: { files: [file] }
        };
        await handleImageUpload(syntheticEvent);
        return;
      }
    }

    // Check if it's a dragged item from wardrobe
    const src = draggedItemSrc || e.dataTransfer.getData('text/plain');
    if (src && (src.startsWith('http') || src.startsWith('/'))) {
      addEssentialItem(src);
      setDraggedItemSrc(null);
    }
  };

  // Set document title for Next.js 15 (client component)
  useEffect(() => {
    document.title = 'Free AI Outfit Generator & Outfit Creator | Provamoda';
  }, []);

  return (
    <div className={`outfit-workspace h-screen bg-[#0e0f13] text-neutral-200 selection:bg-blue-500/30 selection:text-blue-200 overflow-hidden flex flex-col`}>
      <h1 className="sr-only">Free outfit creator</h1>
      <PlannerHeaderActions>
        <div className="planner-header-actions">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="planner-export" aria-label="Export outfit">
                <Download size={14} className="sm:w-4 sm:h-4" />
                <span className="planner-action-label">Export</span>
                <ChevronDown size={12} className="hidden sm:block sm:w-3.5 sm:h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-[#111] border-white/10 text-neutral-200">
              <DropdownMenuItem onClick={handleExport} className="cursor-pointer hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white">
                <Download size={14} className="mr-2" />
                Export JPG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowExportDialog(true)} className="cursor-pointer hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white">
                <Instagram size={14} className="mr-2" />
                Instagram Story
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button 
            onClick={() => setShowConversionModal(true)}
            className="btn sm planner-generate"
            aria-label="Generate model from your outfit"
          >
            <Sparkles size={14} className="sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Generate Model</span>
            <span className="sm:hidden">Model</span>
          </button>
        </div>
      </PlannerHeaderActions>

      {/* --- Main Workspace --- */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden relative h-full min-h-0">
        
        {/* Left Sidebar: Tools & Assets */}
        <aside className="w-full lg:w-80 border-r border-white/5 bg-[#14161d] flex flex-col overflow-hidden z-20 h-auto lg:h-full max-h-[40vh] lg:max-h-full order-2 lg:order-1 flex-shrink-0 lg:flex-shrink">
          <div className="p-4 sm:p-6 border-b border-white/5 flex-shrink-0">
            <h2 className="text-white font-medium mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
              <Upload size={16} className="sm:w-[18px] sm:h-[18px] text-blue-400" />
              Add Items
            </h2>
            
            {/* Magic Upload Area */}
            <label className="relative group cursor-pointer block">
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleImageUpload} 
                className="hidden" 
                disabled={isProcessing}
              />
              <div 
                className="h-24 sm:h-32 rounded-xl border border-dashed border-white/20 bg-white/5 hover:bg-white/10 transition-all flex flex-col items-center justify-center gap-2 group-hover:border-blue-500/50 group-hover:shadow-[0_0_30px_-10px_rgba(139,92,246,0.2)]"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="relative w-full h-full flex flex-col items-center justify-center gap-1 sm:gap-2 min-h-[96px] sm:min-h-[128px]">
                  {isProcessing && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-blue-400 transition-opacity duration-300 ease-in-out opacity-100">
                      <Loader2 size={20} className="sm:w-6 sm:h-6 animate-spin" />
                      <span className="text-[10px] sm:text-xs font-medium">Removing Background...</span>
                    </div>
                  )}
                  <div className={`flex flex-col items-center gap-1 sm:gap-2 transition-opacity duration-300 ease-in-out ${isProcessing ? 'opacity-0' : 'opacity-100'}`}>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:scale-110 transition-transform">
                      <Scissors size={16} className="sm:w-5 sm:h-5" />
                    </div>
                    <div className="text-center">
                      <span className="text-xs sm:text-sm text-white font-medium block">Drop & Remove BG</span>
                      <span className="text-[9px] sm:text-[10px] text-neutral-500">Supports JPG, PNG, HEIC</span>
                    </div>
                  </div>
                </div>
              </div>
            </label>
          </div>

          {/* Essential Wardrobe List */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 min-h-0 h-0 max-h-[calc(100vh-250px)] sm:max-h-[calc(100vh-300px)]">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <h3 className="text-[10px] sm:text-xs font-mono text-neutral-500 uppercase tracking-wider">Essentials Library</h3>
              <span className="text-[9px] sm:text-[10px] bg-white/10 px-1 sm:px-1.5 py-0.5 rounded text-neutral-400">{ESSENTIAL_WARDROBE.length}</span>
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-2 gap-1.5 sm:gap-2">
              {ESSENTIAL_WARDROBE.map((item) => (
                <button 
                  key={item.id}
                  onClick={() => addEssentialItem(item.src)}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.src)}
                  className="group relative aspect-square rounded-lg bg-white border border-white/5 hover:border-blue-500/50 active:border-blue-500 overflow-hidden transition-all cursor-grab active:cursor-grabbing touch-manipulation min-h-[60px]"
                >
                  <img src={item.src} alt={item.name} draggable={false} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 active:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/90 to-transparent p-1.5 sm:p-2 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity">
                    <span className="text-[9px] sm:text-[10px] text-white truncate block">{item.name}</span>
                  </div>
                  <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 bg-[#0203eb] text-white p-0.5 sm:p-1 rounded-full opacity-0 group-hover:opacity-100 group-active:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all">
                    <Layers size={10} className="sm:w-3 sm:h-3" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Center: Canvas */}
        <div className="flex-1 relative bg-[#0e0f13] flex flex-col order-1 lg:order-2 min-h-[400px] lg:min-h-0">
          
          {/* Canvas Toolbar */}
          <div className="absolute top-3 sm:top-6 left-1/2 -translate-x-1/2 glass-panel rounded-full px-3 sm:px-6 py-1.5 sm:py-2 flex items-center gap-3 sm:gap-6 z-30 border border-white/10 shadow-xl">
            <button onClick={() => moveLayer('up')} className="text-neutral-400 hover:text-white tooltip-trigger" title="Bring Forward">
              <Layers size={14} className="sm:w-[18px] sm:h-[18px] rotate-180" />
            </button>
            <button onClick={() => moveLayer('down')} className="text-neutral-400 hover:text-white" title="Send Backward">
              <Layers size={14} className="sm:w-[18px] sm:h-[18px]" />
            </button>
            <div className="w-px h-3 sm:h-4 bg-white/10"></div>
            <button onClick={undo} className="text-neutral-400 hover:text-white" disabled={history.length === 0}>
              <Undo size={14} className="sm:w-[18px] sm:h-[18px]" />
            </button>
            <button onClick={deleteActiveObject} className="text-red-400 hover:text-red-300">
              <Trash2 size={14} className="sm:w-[18px] sm:h-[18px]" />
            </button>
          </div>

          {/* The Fabric Canvas */}
          <div 
            id="canvas-container" 
            className={`flex-1 w-full h-full relative overflow-hidden bg-[radial-gradient(#1a1a1a_1px,transparent_1px)] [background-size:20px_20px] transition-all min-h-[400px] lg:min-h-0 ${isDraggingOver ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-[#0e0f13] bg-blue-500/10' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <canvas ref={canvasRef} className="w-full h-full" style={{ display: 'block' }} />
            
            {/* Drag Overlay */}
            {isDraggingOver && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 bg-blue-500/5 backdrop-blur-sm">
                <div className="text-center px-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-blue-500/20 border-2 border-dashed border-blue-400 flex items-center justify-center mb-3 sm:mb-4 mx-auto animate-pulse">
                    <Upload size={24} className="sm:w-8 sm:h-8 text-blue-400" />
                  </div>
                  <p className="text-sm sm:text-lg font-medium text-blue-300">Drop to add to canvas</p>
                </div>
              </div>
            )}
            
            {/* Processing Overlay */}
            {isProcessing && !isDraggingOver && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 bg-black/60 backdrop-blur-sm transition-opacity duration-300">
                <div className="text-center px-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-blue-500/20 border-2 border-blue-400 flex items-center justify-center mb-3 sm:mb-4 mx-auto">
                    <Loader2 size={24} className="sm:w-8 sm:h-8 text-blue-400 animate-spin" />
                  </div>
                  <p className="text-sm sm:text-lg font-medium text-blue-300">Processing image...</p>
                  <p className="text-xs sm:text-sm text-neutral-400 mt-2">Removing background</p>
                </div>
              </div>
            )}
            
            {/* Empty State Hint */}
            {!canvas?.getObjects().length && !isDraggingOver && !isProcessing && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center opacity-20 px-4">
                  <Shirt size={48} className="sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4" />
                  <p className="text-base sm:text-xl font-light">Drag items here to start planning</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar: Details & Palette */}
        <aside className="w-full lg:w-72 border-l border-white/5 bg-[#14161d] z-20 flex flex-col order-3 h-auto lg:h-full max-h-[40vh] lg:max-h-full overflow-hidden flex-shrink-0 lg:flex-shrink">
          
          {/* Active Item Color Palette */}
          <div className="p-4 sm:p-6 border-b border-white/5 flex-shrink-0">
            <h2 className="text-white font-medium mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
              <Palette size={16} className="sm:w-[18px] sm:h-[18px] text-blue-400" />
              Color Match
            </h2>
            
            {selectedObject ? (
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-white/5 border border-white/5">
                  {/* Thumbnail of active item */}
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded bg-black overflow-hidden border border-white/10 flex-shrink-0">
                     {/* Note: In a real app, you'd render the specific object preview here */}
                     <div className="w-full h-full flex items-center justify-center bg-neutral-800">
                       <Layers size={16} className="sm:w-5 sm:h-5 text-neutral-600" />
                     </div>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-neutral-400">Selected Item</p>
                    <p className="text-xs sm:text-sm text-white font-medium truncate">Active Layer</p>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] sm:text-xs text-neutral-500 mb-2 sm:mb-3 uppercase tracking-wider font-mono">Extracted Palette</p>
                  <div className="grid grid-cols-4 sm:grid-cols-4 gap-1.5 sm:gap-2">
                    {activePalette.length > 0 ? activePalette.map((color, i) => (
                      <div key={i} className="group relative cursor-pointer">
                        <div 
                          className="w-full aspect-square rounded-md shadow-lg border border-white/5 hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          onClick={() => navigator.clipboard.writeText(color)}
                        ></div>
                        <div className="opacity-0 group-hover:opacity-100 absolute -bottom-6 left-1/2 -translate-x-1/2 text-[9px] sm:text-[10px] bg-black text-white px-1 rounded pointer-events-none whitespace-nowrap">
                          {color}
                        </div>
                      </div>
                    )) : (
                      <div className="col-span-4 text-[10px] sm:text-xs text-neutral-600 italic text-center py-3 sm:py-4">
                        Select an image to extract colors
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 sm:py-10 opacity-40">
                <p className="text-xs sm:text-sm px-2">Select an item on the canvas to see matching colors.</p>
              </div>
            )}
          </div>

          {/* Styling Tips / Conversion Hook */}
          <div className="p-4 sm:p-6 bg-gradient-to-b from-blue-900/10 to-transparent flex-1 min-h-0 overflow-y-auto">
            <h3 className="text-white font-medium mb-2 text-sm sm:text-base">Style Tip</h3>
            <p className="text-xs sm:text-sm text-neutral-400 leading-relaxed mb-4 sm:mb-6">
              Use the "Vintage Denim" jacket with the white tee for a timeless casual look.
            </p>

            <div className="mt-auto p-3 sm:p-4 rounded-xl bg-neutral-900 border border-blue-500/30 relative overflow-hidden group">
              <div className="absolute inset-0 bg-[#0203eb]/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <h4 className="text-white font-medium mb-1 relative z-10 text-sm sm:text-base">Visualize this look</h4>
              <p className="text-[10px] sm:text-xs text-neutral-400 mb-2 sm:mb-3 relative z-10">See this outfit on a real model body type.</p>
              <button 
                onClick={() => setShowConversionModal(true)}
                className="planner-try-button w-full py-1.5 sm:py-2 bg-white text-black text-[10px] sm:text-xs font-bold rounded uppercase tracking-wider hover:bg-neutral-200 transition-colors relative z-10"
              >
                Try Provamoda
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* --- Conversion Modal (The Hook) --- */}
      {showConversionModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200 overflow-y-auto">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl sm:rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl relative my-auto">
            <button 
              onClick={() => setShowConversionModal(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-white hover:text-white transition-colors z-20 text-sm sm:text-base px-2 py-1"
            >
              Close
            </button>

            <div className="grid md:grid-cols-2 min-h-[400px] sm:min-h-[500px]">
              <div className="p-6 sm:p-8 flex flex-col justify-center relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-blue-400"></div>
                <h2 className="text-2xl sm:text-3xl font-display font-medium text-white mb-2">
                  Flat lays are nice.<br />
                  <span className="text-blue-400">Models sell.</span>
                </h2>
                <p className="text-neutral-400 text-xs sm:text-sm leading-relaxed mb-6 sm:mb-8">
                  You've designed the perfect look. Now, let Provamoda's AI generate a realistic photoshoot of a model wearing this exact outfit in seconds.
                </p>
                
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-white">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-blue-400/20 text-blue-400 flex items-center justify-center flex-shrink-0"><Zap size={10} className="sm:w-3 sm:h-3" /></div>
                    <span>Retain garment texture & details</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-white">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-blue-400/20 text-blue-400 flex items-center justify-center flex-shrink-0"><Zap size={10} className="sm:w-3 sm:h-3" /></div>
                    <span>Choose ethnicity, size, and pose</span>
                  </div>
                </div>

                <button 
                  onClick={handleGenerateModel}
                  className="mt-6 sm:mt-8 w-full py-2.5 sm:py-3 bg-[#0203eb] hover:bg-[#282af5] text-white text-sm sm:text-base font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Generate AI Model
                    <ArrowRight size={14} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              </div>

              {/* Visual Comparison in Modal */}
              <div className="relative bg-neutral-900 hidden md:block min-h-[300px]">
                <img 
                  src="/plannerItems/result2.webp" 
                  className="absolute inset-0 w-full h-full object-cover opacity-60" 
                  alt="Model"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent"></div>
                <div className="absolute bottom-6 sm:bottom-8 left-6 sm:left-8 right-6 sm:right-8">
                  <div className="bg-black/60 backdrop-blur p-2 sm:p-3 rounded-lg border border-white/10 flex gap-2 sm:gap-3">
                  <div className="w-20 h-10 relative overflow-hidden">
                    <Image src="/plannerItems/example2.jpg" className="object-cover object-center rounded-md" alt="Model" fill/>
                    </div>
                        <div className="flex-1 flex items-center justify-end">
                        <span className="text-[10px] sm:text-xs text-blue-400 font-mono">PROCESSING COMPLETE</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-md max-w-[calc(100vw-2rem)] mx-4">
          <DialogHeader>
            <DialogTitle className="text-white text-lg sm:text-xl">Export for Instagram</DialogTitle>
            <DialogDescription className="text-neutral-400 text-xs sm:text-sm">
              Create a story-ready 3:4 image. Add a title to appear on your layout.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="title" className="text-xs sm:text-sm font-medium text-neutral-300">
                Outfit Title (Optional)
              </label>
              <Input
                id="title"
                placeholder="e.g. Date Night Look"
                value={exportTitle}
                onChange={(e) => setExportTitle(e.target.value)}
                className="bg-neutral-900 border-white/10 text-white text-sm placeholder:text-neutral-600 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="color-grid"
                checked={showColorGrid}
                onCheckedChange={(checked) => setShowColorGrid(checked)}
                className="border-white/20 data-[state=checked]:bg-[#0203eb] data-[state=checked]:border-blue-600"
              />
              <label
                htmlFor="color-grid"
                className="text-xs sm:text-sm font-medium text-neutral-300 cursor-pointer"
              >
                Include color palette at bottom
              </label>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setShowExportDialog(false)} className="text-neutral-400 hover:text-white hover:bg-white/5 w-full sm:w-auto text-sm">
              Cancel
            </Button>
            <Button onClick={handleInstagramExport} className="bg-[#0203eb] hover:bg-[#282af5] text-white w-full sm:w-auto text-sm">
              Export Story
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}